import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './contact.css';

const LOGO = `${process.env.PUBLIC_URL}/g2-logo.png`;

const LD_CONTACT = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Groupardy',
  url: 'https://groupardy.com/contact',
  description: 'Get in touch with the Groupardy team — general questions, support and bug reports, press, and partner inquiries.',
});

const LD_ORG = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Groupardy',
  url: 'https://groupardy.com/',
  logo: 'https://groupardy.com/g2-logo.png',
  email: 'hello@groupardy.com',
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@groupardy.com',
      availableLanguage: ['English'],
    },
    {
      '@type': 'ContactPoint',
      contactType: 'press',
      email: 'press@groupardy.com',
      availableLanguage: ['English'],
    },
  ],
});

const Contact = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="contact">
      <Helmet>
        <title>Contact Groupardy · Say hi, report a bug, or pitch a partnership</title>
        <meta name="description" content="Get in touch with the Groupardy team. Email us about your game night, a bug you hit, a venue you want to host at, or a story you're writing." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://groupardy.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://groupardy.com/contact" />
        <meta property="og:title" content="Contact Groupardy" />
        <meta property="og:description" content="Email the Groupardy team — general questions, support and bug reports, press, and venue partnerships." />
        <meta property="og:site_name" content="Groupardy" />
        <meta property="og:image" content="https://groupardy.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Groupardy" />
        <meta name="twitter:description" content="Email the Groupardy team. Questions, bugs, press, partnerships — pick the right inbox." />
        <meta name="twitter:image" content="https://groupardy.com/og-image.jpg" />
        <meta name="theme-color" content="#2ec4b6" />
        <script type="application/ld+json">{LD_CONTACT}</script>
        <script type="application/ld+json">{LD_ORG}</script>
      </Helmet>

      {/* ─── NAV ─── */}
      <header className="co-nav">
        <div className="co-wrap co-nav-inner">
          <a className="co-nav-brand" href="/" aria-label="Groupardy home">
            <img src={LOGO} alt="Groupardy logo" className="co-nav-logo" />
            <span className="co-nav-wordmark">Groupardy</span>
          </a>
          <nav className="co-nav-links" aria-label="Primary">
            <a className="co-nav-link" href="/#how">How it works</a>
            <a className="co-nav-link" href="/#use-cases">Use cases</a>
            <a className="co-nav-link" href="/#host">AI host</a>
            <a className="co-nav-link" href="/#faq">FAQ</a>
          </nav>
          <div className="co-nav-actions">
            <a className="co-nav-signin" href="/login">Sign in</a>
            <a className="co-btn co-btn-primary co-btn-sm" href="/create">
              Start a game
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* ─── HERO ─── */}
        <section className="co-hero" aria-labelledby="contact-title">
          <div className="co-wrap co-hero-inner">
            <div className="co-hero-eyebrow">
              <span className="co-pulse-dot" /> Inbox · Open
            </div>
            <h1 id="contact-title">
              Say hi. Or ask a question.<br />
              Or send us a <span className="co-underlined">bug report</span>.
            </h1>
            <p className="co-hero-lead">
              We read everything. We mostly write back the same day — sometimes between rounds.
            </p>
          </div>
        </section>

        {/* ─── CONTACT CARDS ─── */}
        <section className="co-section">
          <div className="co-wrap">
            <div className="co-cards">
              <a className="co-card co-card-primary" href="mailto:hello@groupardy.com">
                <div className="co-card-icon" aria-hidden="true">✉️</div>
                <div className="co-card-eyebrow">General</div>
                <h3>Just say hi</h3>
                <p>
                  Questions about Groupardy, ideas for boards, a story about a game night
                  that got out of hand — this is the one.
                </p>
                <div className="co-card-email">hello@groupardy.com</div>
                <span className="co-btn co-btn-primary co-btn-lg co-card-cta">
                  Email us
                </span>
              </a>

              <a className="co-card" href="mailto:support@groupardy.com?subject=Groupardy%20bug%20report">
                <div className="co-card-icon co-icon-gold" aria-hidden="true">🛠</div>
                <div className="co-card-eyebrow">Support &amp; bugs</div>
                <h3>Something broke</h3>
                <p>
                  Stuck mid-game, weird host narration, a QR code that won't scan.
                  Tell us what happened and we'll dig in.
                </p>
                <div className="co-card-email">support@groupardy.com</div>
                <span className="co-card-hint">
                  Include the game URL if you have one.
                </span>
              </a>

              <a className="co-card" href="mailto:press@groupardy.com?subject=Press%20inquiry">
                <div className="co-card-icon co-icon-lavender" aria-hidden="true">🎤</div>
                <div className="co-card-eyebrow">Press</div>
                <h3>Writing a story</h3>
                <p>
                  Interviews, screenshots, embargoed demos, and anything else a reporter
                  or podcaster might need. Happy to chat.
                </p>
                <div className="co-card-email">press@groupardy.com</div>
                <span className="co-card-hint">
                  We can also share a press kit on request.
                </span>
              </a>
            </div>
          </div>
        </section>

        {/* ─── PARTNERSHIPS CALLOUT ─── */}
        <section className="co-section co-partners-band" id="partners">
          <div className="co-wrap">
            <div className="co-partners">
              <div>
                <div className="co-section-eyebrow">Venues &amp; partnerships</div>
                <h2>Running Groupardy at a bar, classroom, or office?</h2>
                <p className="co-sub">
                  We've worked with neighborhood bars on weekly trivia nights, with HR teams on
                  onboarding boards, and with conferences on opening-night icebreakers. If you're
                  thinking about something bigger than one game, write to{' '}
                  <a className="co-inline-link" href="mailto:hello@groupardy.com?subject=Partnership%20inquiry">
                    hello@groupardy.com
                  </a>{' '}
                  with a couple of sentences about your group — we'll take it from there.
                </p>
              </div>
              <a
                className="co-btn co-btn-gold co-btn-lg"
                href="mailto:hello@groupardy.com?subject=Partnership%20inquiry"
              >
                Pitch a partnership
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* ─── FOOTER ─── */}
      <footer className="co-footer">
        <div className="co-wrap">
          <div className="co-foot-grid">
            <div className="co-foot-brand">
              <div className="co-wm">
                <img src={LOGO} alt="" style={{ width: 28, height: 28 }} />
                Groupardy
              </div>
              <p>The AI-powered Jeopardy-style game show for groups. A real game show for the room you're already in.</p>
              <div className="co-foot-socials">
                <a href="https://www.instagram.com/groupardy/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">📷</a>
                <a href="https://x.com/groupardy" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">𝕏</a>
              </div>
            </div>
            <div className="co-foot-col">
              <h5>Play</h5>
              <ul>
                <li><a href="/create">Start a game</a></li>
              </ul>
            </div>
            <div className="co-foot-col">
              <h5>Product</h5>
              <ul>
                <li><a href="/#how">How it works</a></li>
                <li><a href="/#host">The AI host</a></li>
              </ul>
            </div>
            <div className="co-foot-col">
              <h5>Company</h5>
              <ul>
                <li><a href="/about">About</a></li>
                <li><a href="/blog">Blog</a></li>
                <li><a href="/press">Press</a></li>
                <li><a href="/contact">Contact</a></li>
              </ul>
            </div>
            <div className="co-foot-col">
              <h5>Support</h5>
              <ul>
                <li><a href="/help">Help center</a></li>
                <li><a href="/#faq">FAQ</a></li>
                <li><a href="/terms">Terms</a></li>
                <li><a href="/privacy">Privacy</a></li>
              </ul>
            </div>
          </div>
          <div className="co-foot-bar">
            <div>© 2026 Groupardy. All rights reserved.</div>
            <div>Made with ❤️ for the group chat that always says "we should hang out more".</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
